import React from "react";
import { createProduct } from "@payping/redux/actions";
import { Button, Checkbox, Form, Input, Modal, Space, TextArea, useMessagedAsyncFn } from "@payping/web-components";
import { ProductCreateViewModel, ProductDetailViewModel } from "@payping/api";

import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppDispatch } from "@/utils/redux";
import { Product } from "./type";
import { CuToman } from "@payping/icons";

interface Props {
  visible: boolean;
  onCancel(): void;
  onOk(productData: Product): void;
}

const CreateProductModal: React.FC<Props> = ({ visible, onCancel, onOk }) => {
  const { t } = useAppTranslation();
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();

  const [{ loading }, handleCreate] = useMessagedAsyncFn(async (productData: ProductDetailViewModel) => {
    const data: ProductCreateViewModel = {
      ...productData,
      amount: parseInt(productData.amount as any, 10),
      // quantity: 1,
      unlimited: true,
      defineAmountByUser: false,
    };
    const productCode = await dispatch(createProduct(data));
    onOk({ code: productCode, ...data });
    return productCode;
  }, []);

  const footer = (
    <Space>
      <Button onClick={onCancel}>{t("cancel")}</Button>
      <Button loading={loading} onClick={form.submit} type={"primary"}>
        {t("product.create")}
      </Button>
    </Space>
  );

  return (
    <Modal open={visible} title={t("product.create")} onCancel={onCancel} footer={footer}>
      <Form
        onFinish={async (values) => {
          try {
            await handleCreate(values as ProductDetailViewModel);
          } catch {
            /* empty */
          }
        }}
        form={form}
        layout={"vertical"}
      >
        <Form.Item label={t("product.name")} rules={[{ required: true }]} name={"title"}>
          <Input />
        </Form.Item>
        <Form.Item label={t("product.description")} rules={[{ required: true }]} name={"description"}>
          <TextArea />
        </Form.Item>
        <Form.Item
          label={t("price")}
          rules={[
            { required: true },
            {
              validator: (_, value): Promise<void> =>
                value && !(parseInt(value, 10) >= 1000)
                  ? Promise.reject(new Error(t("error.minimum", { quantity: 1000 })))
                  : Promise.resolve(),
            },
          ]}
          name={"amount"}
        >
          <Input.Number
            className="tw-w-full"
            suffix={<CuToman width={24} height={24} />}
            separateThousandth
            inputMode="numeric"
            changeOnWheel={false}
            controls={false}
          />
        </Form.Item>

        <Form.Item name={"haveTax"} valuePropName={"checked"}>
          <Checkbox>{t("invoice.form.tax.label")}</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateProductModal;
